import React, { useEffect } from "react";
import {
  LegalContainer,
  LegalInner,
  HeaderTitle,
  Subtle,
  BoxDiv,
  SectionTitle,
  SubTitle,
  CrossLinks,
  PageLink,
} from "../legal/legalStyles";

const LAST_UPDATED = "26 May 2026";
const CONTACT_EMAIL = "nassos@duck.com";

const PrivacyPage = () => {
  useEffect(() => {
    document.title = "NΛSOS — Privacy Policy";
  }, []);

  return (
    <LegalContainer>
      <LegalInner>
        <HeaderTitle>Privacy Policy</HeaderTitle>
        <Subtle>Last updated: {LAST_UPDATED}</Subtle>
        <CrossLinks>
          <PageLink to="/cookies">Cookie Policy →</PageLink>
        </CrossLinks>

        <BoxDiv>
          <p>
            This Privacy Policy explains what personal data <strong>n450s.com</strong> ("the
            Site", "we", "us") collects about you, why, and what rights you have over it. We
            try to collect as little as possible. We do <strong>not</strong> run advertising,
            we do <strong>not</strong> profile you, we do <strong>not</strong> sell your data,
            and we use <strong>no analytics or tracking cookies</strong> — which is why you
            will not see a cookie consent banner. For the details on the few strictly-necessary
            cookies we do use, see our <PageLink to="/cookies">Cookie Policy</PageLink>.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>1. Who is responsible for your data</SectionTitle>
          <p>
            The Site is a personal, non-commercial project operated under the username{" "}
            <strong>n450s</strong>, the registrant of the <strong>n450s.com</strong> domain
            (the "data controller"). The operator is based in Greece (European Union), so this
            policy is written to comply with the EU General Data Protection Regulation (GDPR)
            and Greek Law 4624/2019.
          </p>
          <p>
            For any privacy question or to exercise your rights, contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>2. Scope</SectionTitle>
          <p>
            This policy covers the n450s.com website and its connected services (accounts and
            single sign-on, Art of the Day, the art gallery, coffee cards, badges, the store,
            respect cards, and the AI chat). Some of these features are optional and only
            process data if you choose to use them.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>3. What data we collect</SectionTitle>

          <SubTitle>Information you give us</SubTitle>
          <ul>
            <li>
              <strong>Account &amp; identity:</strong> username, email address, and a hashed
              password when you register or sign in through our single sign-on. Optionally any
              profile details you choose to add.
            </li>
            <li>
              <strong>Content you create:</strong> art submissions, coffee ratings and shop
              details, comments, messages you send in chat, and similar contributions.
            </li>
            <li>
              <strong>Optional connections:</strong> a Telegram account if you link one, and a
              browser push subscription if you enable notifications.
            </li>
          </ul>

          <SubTitle>Information collected automatically</SubTitle>
          <ul>
            <li>
              <strong>Technical &amp; security data:</strong> your IP address, browser/device
              type, and basic request logs, kept for operating and securing the Site.
            </li>
            <li>
              <strong>Authentication state:</strong> a strictly-necessary session cookie and
              short-lived login security values, described in the{" "}
              <PageLink to="/cookies">Cookie Policy</PageLink>.
            </li>
            <li>
              <strong>Location (only on request):</strong> if you use coffee-shop location
              features, your browser may ask permission to share your location with you-side
              map/geocoding features.
            </li>
          </ul>
          <p>
            We do <strong>not</strong> use analytics, advertising trackers, or behavioural
            profiling. We do not collect special-category (sensitive) data.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>4. Why we use it, and our legal basis</SectionTitle>
          <ul>
            <li>
              <strong>To run your account and provide the service</strong> — legal basis:
              performance of a contract (GDPR Art. 6(1)(b)).
            </li>
            <li>
              <strong>To keep the Site secure and prevent spam, bots and abuse</strong> (server
              logs, reCAPTCHA) — legal basis: our legitimate interests in a safe service (Art.
              6(1)(f)).
            </li>
            <li>
              <strong>To send notifications you opt into</strong> (browser push, Telegram,
              email) — legal basis: your consent (Art. 6(1)(a)), which you can withdraw at any
              time.
            </li>
            <li>
              <strong>To comply with the law</strong> where required — legal basis: legal
              obligation (Art. 6(1)(c)).
            </li>
          </ul>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>5. Cookies and local storage</SectionTitle>
          <p>
            We only use cookies and browser storage that are strictly necessary to run the Site
            or to remember preferences on your own device. There are no analytics or advertising
            cookies, so no consent banner is required. The full list — what each item is, why it
            exists, and how long it lasts — is in our{" "}
            <PageLink to="/cookies">Cookie Policy</PageLink>.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>6. Who we share data with (service providers)</SectionTitle>
          <p>
            We do not sell your personal data and we do not share it for advertising. We rely on
            a small number of service providers ("processors") strictly to operate the Site:
          </p>
          <ul>
            <li>
              <strong>Hosting:</strong> our servers run on Hetzner (data centres in Germany,
              EU); the website front-end is served via Netlify.
            </li>
            <li>
              <strong>Google reCAPTCHA:</strong> Google Ireland/LLC, used only on submission
              forms (e.g. coffee ratings, comments) to block bots. Subject to Google&apos;s own
              privacy terms.
            </li>
            <li>
              <strong>Google Maps Platform:</strong> used only when you interact with
              coffee-shop location/geocoding features.
            </li>
            <li>
              <strong>Telegram:</strong> only if you link your Telegram account for messages.
            </li>
            <li>
              <strong>Browser push services</strong> (e.g. Mozilla, Google, or Apple push
              endpoints): only if you enable browser notifications.
            </li>
          </ul>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>7. International transfers</SectionTitle>
          <p>
            Our core infrastructure is hosted in the EU (Germany). Some providers above (for
            example Google and Netlify) may process limited data outside the EU, including in
            the United States. Where that happens, transfers rely on appropriate safeguards such
            as the EU Standard Contractual Clauses and/or the EU–US Data Privacy Framework.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>8. How long we keep your data</SectionTitle>
          <p>
            We keep account and content data for as long as your account exists. Technical and
            security logs are kept only for a short period needed for security and operation. If
            you delete your account, we delete or anonymise the personal data associated with
            it, except where we must retain it to meet a legal obligation.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>9. Your rights</SectionTitle>
          <p>Under the GDPR you have the right to:</p>
          <ul>
            <li>access the personal data we hold about you;</li>
            <li>have inaccurate data corrected;</li>
            <li>have your data erased ("right to be forgotten");</li>
            <li>restrict or object to certain processing;</li>
            <li>receive your data in a portable format;</li>
            <li>withdraw consent at any time, where processing is based on consent.</li>
          </ul>
          <p>
            To exercise any of these, email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. You also have the right to
            lodge a complaint with your local supervisory authority. In Greece this is the
            Hellenic Data Protection Authority (HDPA),{" "}
            <a href="https://www.dpa.gr" target="_blank" rel="noopener noreferrer">
              www.dpa.gr
            </a>
            .
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>10. Security</SectionTitle>
          <p>
            We protect data in transit with HTTPS/TLS, store passwords only as salted hashes,
            and keep authentication tokens in secure, HttpOnly cookies. No method of
            transmission or storage is 100% secure, but we take reasonable measures to protect
            your information.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>11. Children</SectionTitle>
          <p>
            The Site is not intended for children under 15 (the digital-consent age under Greek
            law). We do not knowingly collect personal data from children under that age. If you
            believe a child has provided us data, contact us and we will remove it.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>12. Changes to this policy</SectionTitle>
          <p>
            We may update this policy from time to time. We will post the new version here and
            update the "Last updated" date above. Material changes may also be announced on the
            Site.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>13. Contact</SectionTitle>
          <p>
            Questions about this policy or your data? Email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </BoxDiv>

        <CrossLinks>
          <PageLink to="/cookies">Cookie Policy →</PageLink>
        </CrossLinks>
      </LegalInner>
    </LegalContainer>
  );
};

export default PrivacyPage;
